/* global debounce */

/**
 * Google Maps Dynamic Library Import Bootstrap
 * 这是Google官方提供的bootstrap代码，用于启用Dynamic Library Import功能
 *
 * @param {string} apiKey - Google Maps API Key
 */
function initGoogleMapsLoader(apiKey) {
  const namespace = 'google'
  window[namespace] = window[namespace] || {}
  const googleNamespace = window[namespace]

  googleNamespace.maps = googleNamespace.maps || {}
  const mapsNamespace = googleNamespace.maps

  const librariesToLoad = new Set()
  const urlParams = new URLSearchParams()

  let loadPromise = null
  const loadScript = () => {
    if (loadPromise) {
      return loadPromise
    }

    loadPromise = new Promise(resolve => {
      const scriptElement = document.createElement('script')

      urlParams.set('libraries', Array.from(librariesToLoad).join(','))
      urlParams.set('key', apiKey)
      urlParams.set('v', 'weekly')
      urlParams.set('language', 'en')
      urlParams.set('region', 'US')
      urlParams.set('callback', 'google.maps.__ib__')

      scriptElement.src = `https://maps.googleapis.com/maps/api/js?${urlParams}`
      mapsNamespace.__ib__ = resolve

      const existingNonce = document.querySelector('script[nonce]')?.nonce || ''
      if (existingNonce) {
        scriptElement.nonce = existingNonce
      }

      document.head.append(scriptElement)
    })

    return loadPromise
  }

  if (mapsNamespace.importLibrary) {
    console.warn('The Google Maps JavaScript API only loads once. Ignoring')
    return
  }

  mapsNamespace.importLibrary = function (libraryName, ...rest) {
    librariesToLoad.add(libraryName)
    return loadScript().then(() => mapsNamespace.importLibrary(libraryName, ...rest))
  }
}

initGoogleMapsLoader(window.GOOGLE_MAPS_API_KEY)

class GoogleMapSection extends HTMLElement {
  // 静态常量 - 避免每个实例重复创建
  static ICON_SRC = 'https://cdn.shopify.com/s/files/1/0078/6156/7570/files/google-pin-icon.webp'
  static DISABLED_ICON_SRC = 'https://cdn.shopify.com/s/files/1/0078/6156/7570/files/google-pin-icon-disabled.webp'
  static ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/
  static BREAKPOINTS = { MOBILE: 767, TABLET: 1024 }

  constructor() {
    super()

    // DOM 元素引用
    this.input = null
    this.searchButton = null
    this.resultsSummary = null
    this.resultsCount = null
    this.resultsZip = null
    this.emptyInput = null
    this.searchZipCode = null
    this.resultsList = null
    this.resultsContainer = null

    // 缓存的模板引用
    this._resultTemplate = null
    this._infoWindowTemplate = null

    // Google Maps 相关
    this.markers = []
    this.markerListeners = [] // 追踪 marker 事件监听器
    this.map = null
    this.mapClickListener = null
    this.infoWindow = null

    // 缓存的图标配置对象 - 避免重复创建
    this._activeIcon = null
    this._disabledIcon = null
    this._scaledSize = null

    // 存储安装商数据用于事件委托
    this._installersData = []

    // 请求取消控制器
    this._abortController = null

    // 预绑定事件处理函数 - 避免每次 addEventListener 创建新函数
    this._boundHandleInput = debounce(this.handleInput.bind(this), 150)
    this._boundHandleKeyDown = this.handleKeyDown.bind(this)
    this._boundHandleSearch = this.handleSearch.bind(this)
    this._boundHandleResultClick = this.handleResultClick.bind(this)
  }

  connectedCallback() {
    this.initElements()
    this.initMap()

    // 添加事件监听器
    this.input?.addEventListener('input', this._boundHandleInput)
    this.input?.addEventListener('keydown', this._boundHandleKeyDown)
    this.searchButton?.addEventListener('click', this._boundHandleSearch)

    // 使用事件委托处理结果列表点击
    this.resultsList?.addEventListener('click', this._boundHandleResultClick)
  }

  disconnectedCallback() {
    // 移除事件监听器
    this.input?.removeEventListener('input', this._boundHandleInput)
    this.input?.removeEventListener('keydown', this._boundHandleKeyDown)
    this.searchButton?.removeEventListener('click', this._boundHandleSearch)
    this.resultsList?.removeEventListener('click', this._boundHandleResultClick)

    // 取消进行中的请求
    this._abortController?.abort()

    // 清理 Google Maps 相关资源
    this.clearMarkers()

    if (this.mapClickListener) {
      window.google?.maps?.event?.removeListener(this.mapClickListener)
      this.mapClickListener = null
    }

    if (this.infoWindow) {
      this.infoWindow.close()
      this.infoWindow = null
    }

    // 清空引用
    this.map = null
    this._installersData = []
    this._resultTemplate = null
    this._infoWindowTemplate = null
  }

  initElements() {
    this.input = this.querySelector('#google-map-search-input')
    this.searchButton = this.querySelector('.google-map-section__search-button')
    this.resultsSummary = this.querySelector('.google-map-section__results-summary')
    this.resultsCount = this.querySelector('#google-map-results-count')
    this.resultsZip = this.querySelector('#google-map-results-zip')
    this.emptyInput = this.querySelector('.google-map-section__empty-input')
    this.searchZipCode = this.querySelector('#google-map-empty-search-zip')
    this.resultsList = this.querySelector('#google-map-results-list')
    this.resultsContainer = this.querySelector('.google-map-section__results-container')

    // 缓存模板引用
    this._resultTemplate = this.querySelector('#google-map-section__result-template')
    this._infoWindowTemplate = document.getElementById('google-map-section__info-window-template')
  }

  /**
   * 检查视口宽度是否小于等于指定值
   * @param {number} width
   * @returns {boolean}
   */
  isViewportBelow(width) {
    return window.innerWidth <= width
  }

  /**
   * 获取根据视口宽度计算的缩放级别
   * @returns {number}
   */
  getInitialZoomLevel() {
    if (this.isViewportBelow(GoogleMapSection.BREAKPOINTS.MOBILE)) return 2
    if (this.isViewportBelow(GoogleMapSection.BREAKPOINTS.TABLET)) return 4
    return 5
  }

  async initMap() {
    if (this.map || !this.isConnected) return
    if (!window.google?.maps?.importLibrary) {
      console.error('Google Maps loader is not initialized')
      return
    }

    const { Map } = await window.google.maps.importLibrary('maps')
    if (!this.isConnected || this.map) return

    const mapElement = document.getElementById('google-map')
    if (!mapElement) {
      console.error('Map element not found')
      return
    }

    // 创建地图实例
    this.map = new Map(mapElement, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: this.getInitialZoomLevel(),
      minZoom: 3,
      maxZoom: 18,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      language: 'en'
    })

    this.mapClickListener = this.map.addListener('click', () => {
      this.closeInfoWindow()
    })

    // 缓存图标配置对象
    this._scaledSize = new window.google.maps.Size(34, 34)
    this._activeIcon = { url: GoogleMapSection.ICON_SRC, scaledSize: this._scaledSize }
    this._disabledIcon = { url: GoogleMapSection.DISABLED_ICON_SRC, scaledSize: this._scaledSize }
  }

  /**
   * 验证邮编格式
   * @param {string} zipCode
   * @returns {boolean}
   */
  checkZipCodeIsValid(zipCode) {
    const value = zipCode.trim()
    return value !== '' && GoogleMapSection.ZIP_CODE_REGEX.test(value)
  }

  handleInput(event) {
    const isValid = this.checkZipCodeIsValid(event.target.value)
    this.searchButton.disabled = !isValid
    event.target.setAttribute('aria-invalid', isValid ? 'false' : 'true')
  }

  handleKeyDown(event) {
    if (event.key !== 'Enter') return
    this.searchInstallers(event.target.value)
  }

  handleSearch() {
    this.searchInstallers(this.input.value)
  }

  /**
   * 事件委托处理结果列表点击
   * @param {Event} event
   */
  handleResultClick(event) {
    const resultWrapper = event.target.closest('.google-map-section__result-wrapper')
    if (!resultWrapper) return

    const index = parseInt(resultWrapper.dataset.index, 10)
    if (isNaN(index) || index < 0 || index >= this._installersData.length) return

    const installer = this._installersData[index]
    const marker = this.markers[index]

    this.closeInfoWindow()
    this.highlightMarker(marker, installer)
    this.highlightResult(index)

    if (!this.isViewportBelow(GoogleMapSection.BREAKPOINTS.MOBILE)) {
      this.openInfoWindow(marker, installer)
    }
  }

  closeInfoWindow() {
    if (!this.infoWindow) return
    this.infoWindow.close()

    // 批量重置所有 marker 图标
    const markersLength = this.markers.length
    for (let i = 0; i < markersLength; i++) {
      this.markers[i].setIcon(this._disabledIcon)
    }
  }

  async searchInstallers(zipCode) {
    const isValid = this.checkZipCodeIsValid(zipCode)
    if (!isValid) return

    // 取消之前的请求
    this._abortController?.abort()
    this._abortController = new AbortController()

    this.clearMarkers()
    this.classList.add('loading')
    this.resultsContainer.ariaBusy = true

    try {
      const response = await fetch(
        `https://plumber.waterdropfilter.com:8000/api/installers?zip=${encodeURIComponent(zipCode)}`,
        { signal: this._abortController.signal }
      )

      const result = await response.json()

      if (!response.ok) {
        this.classList.add('hide-results')
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const { detail = [], total = 0 } = result.data

      if (detail.length === 0 || total === 0) {
        this.classList.remove('loading')
        this.classList.add('empty-results')
        this.resultsContainer.ariaBusy = false
        this.searchZipCode.textContent = zipCode
        return
      }

      this.classList.remove('empty-results')
      this.resultsCount.textContent = total
      this.resultsZip.textContent = zipCode

      // 复用或创建 InfoWindow
      if (!this.infoWindow) this.infoWindow = new window.google.maps.InfoWindow()

      // 存储安装商数据用于事件委托
      this._installersData = detail

      // 使用 DocumentFragment 批量插入 DOM
      const fragment = document.createDocumentFragment()

      // 预解析坐标，避免重复 parseFloat
      const parsedCoords = detail.map(installer => ({ lat: parseFloat(installer.lat), lng: parseFloat(installer.lng) }))

      // 创建标记和结果项
      const detailLength = detail.length
      for (let index = 0; index < detailLength; index++) {
        const installer = detail[index]
        const coords = parsedCoords[index]
        const marker = this.createMarker(installer, index, coords)
        this.markers.push(marker)
        this.renderResultToFragment(fragment, installer, index)
      }

      // 清空并批量插入结果列表
      this.resultsList.replaceChildren(fragment)

      // 设置地图中心和缩放
      this.map.setCenter(parsedCoords[0])
      this.map.setZoom(10)
    } catch (error) {
      // 忽略取消的请求
      if (error.name === 'AbortError') return

      console.error('Error searching installers', error)
      this.classList.add('hide-results')
    } finally {
      this.emptyInput?.classList.add('hidden')
      this.classList.remove('loading')
      this.classList.remove('hide-results')
      this.resultsContainer.ariaBusy = false
      this.resultsContainer?.slider?.refresh()
    }
  }

  /**
   * 创建地图标记
   * @param {Object} installer
   * @param {number} index
   * @param {Object} coords - 预解析的坐标 {lat, lng}
   * @returns {google.maps.Marker}
   */
  createMarker(installer, index, coords) {
    const marker = new window.google.maps.Marker({
      position: coords,
      map: this.map,
      optimized: true, // 启用优化以提升性能
      icon: this._disabledIcon
    })

    const listener = marker.addListener('click', () => {
      this.closeInfoWindow()
      this.highlightMarker(marker, installer)
      this.highlightResult(index)

      if (!this.isViewportBelow(GoogleMapSection.BREAKPOINTS.MOBILE)) {
        this.openInfoWindow(marker, installer)
      }
    })

    this.markerListeners.push(listener)
    return marker
  }

  /**
   * 高亮指定的标记
   * @param {google.maps.Marker} activeMarker
   * @param {Object} installer
   */
  highlightMarker(activeMarker, installer) {
    const markersLength = this.markers.length
    for (let i = 0; i < markersLength; i++) {
      const marker = this.markers[i]
      marker.setIcon(marker === activeMarker ? this._activeIcon : this._disabledIcon)
    }
    this.map.setCenter({ lat: parseFloat(installer.lat), lng: parseFloat(installer.lng) })
  }

  /**
   * 高亮指定索引的结果项
   * @param {number} targetIndex
   */
  highlightResult(targetIndex) {
    const resultItems = this.resultsList.children
    const itemsLength = resultItems.length

    for (let i = 0; i < itemsLength; i++) {
      const item = resultItems[i]
      const isActive = i === targetIndex

      item.classList.toggle('google-map-section__result-wrapper--active', isActive)

      if (isActive) {
        if (this.isViewportBelow(GoogleMapSection.BREAKPOINTS.TABLET) && this.resultsContainer?.slider) {
          this.resultsContainer.slider.go(targetIndex)
        } else {
          // 使用 requestAnimationFrame 优化滚动性能
          requestAnimationFrame(() => {
            item.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          })
        }
      }
    }
  }

  /**
   * 打开信息窗口
   * @param {google.maps.Marker} marker
   * @param {Object} installer
   */
  openInfoWindow(marker, installer) {
    if (!this._infoWindowTemplate) return

    const clone = this._infoWindowTemplate.content.cloneNode(true)

    // 填充数据
    this.populateInfoWindowContent(clone, installer)

    // 创建临时容器来获取 HTML 字符串
    const tempDiv = document.createElement('div')
    tempDiv.appendChild(clone)

    this.infoWindow.setHeaderContent()
    this.infoWindow.setContent(tempDiv)
    this.infoWindow.open(marker.getMap(), marker)
  }

  /**
   * 填充信息窗口内容
   * @param {DocumentFragment} clone
   * @param {Object} installer
   */
  populateInfoWindowContent(clone, installer) {
    const titleEl = clone.querySelector('.google-map-section__info-window-title')
    const descriptionEl = clone.querySelector('.google-map-section__info-window-description')
    const telLinkEl = clone.querySelector('.google-map-section__info-window-link-tel')
    const telLinkTextEl = clone.querySelector('.google-map-section__info-window-link-tel-text')
    const websiteLinkEl = clone.querySelector('.google-map-section__info-window-link-website')

    // 标题
    if (titleEl) {
      titleEl.textContent = installer.name || ''
      titleEl.classList.toggle('hide-with-empty', !installer.name)
    }

    // 地址
    if (descriptionEl) {
      descriptionEl.textContent = installer.address || ''
      descriptionEl.classList.toggle('hide-with-empty', !installer.address)
    }

    // 电话链接
    if (telLinkEl) {
      if (installer.phone) {
        if (telLinkTextEl) telLinkTextEl.textContent = installer.phone
        telLinkEl.href = `tel:${installer.phone}`
        telLinkEl.classList.remove('hidden')
      } else {
        telLinkEl.classList.add('hidden')
      }
    }

    // 网站链接
    if (websiteLinkEl) {
      if (installer.url) {
        websiteLinkEl.href = installer.url
        websiteLinkEl.classList.remove('hidden')
      } else {
        websiteLinkEl.classList.add('hidden')
      }
    }

    // 检查是否需要隐藏分隔线
    const divider = clone.querySelector('.google-map-section__info-window-divider')
    const linksContainer = clone.querySelector('.google-map-section__info-window-links')
    if (divider && linksContainer) {
      const hasVisibleLinks = linksContainer.querySelector('a:not(.hidden)')
      if (!hasVisibleLinks) {
        divider.style.display = 'none'
      }
    }
  }

  /**
   * 渲染结果项到 DocumentFragment
   * @param {DocumentFragment} fragment
   * @param {Object} installer
   * @param {number} index
   */
  renderResultToFragment(fragment, installer, index) {
    if (!this._resultTemplate) return

    const clone = this._resultTemplate.content.cloneNode(true)
    const resultWrapper = clone.querySelector('.google-map-section__result-wrapper')

    // 存储索引用于事件委托
    if (resultWrapper) {
      resultWrapper.dataset.index = index
    }

    const labelText = clone.querySelector('.google-map-section__result-label-text')
    const nameEl = clone.querySelector('.google-map-section__result-name')
    const addressEl = clone.querySelector('.google-map-section__result-address')
    const telLink = clone.querySelector('.google-map-section__info-window-link-tel')
    const telLinkText = clone.querySelector('.google-map-section__info-window-link-tel-text')
    const websiteLink = clone.querySelector('.google-map-section__info-window-link-website')

    if (labelText) labelText.textContent = index + 1
    if (nameEl) nameEl.textContent = installer.name || ''
    if (addressEl) addressEl.textContent = installer.address || ''

    if (telLink) {
      if (installer.phone) {
        if (telLinkText) telLinkText.textContent = installer.phone
        telLink.href = `tel:${installer.phone}`
        telLink.classList.remove('hidden')
      } else {
        telLink.classList.add('hidden')
      }
    }

    if (websiteLink) {
      if (installer.url) {
        websiteLink.href = installer.url
        websiteLink.classList.remove('hidden')
      } else {
        websiteLink.classList.add('hidden')
      }
    }

    const divider = clone.querySelector('.google-map-section__info-window-divider')
    const linksContainer = clone.querySelector('.google-map-section__info-window-links')
    if (divider && linksContainer) {
      const hasVisibleLinks = linksContainer.querySelector('a:not(.hidden)')
      if (!hasVisibleLinks) divider.style.display = 'none'
    }

    fragment.appendChild(clone)
  }

  /**
   * 清理所有标记
   */
  clearMarkers() {
    // 移除 marker 事件监听器
    const listenersLength = this.markerListeners.length
    for (let i = 0; i < listenersLength; i++) {
      window.google?.maps?.event?.removeListener(this.markerListeners[i])
    }
    this.markerListeners = []

    // 从地图移除 markers
    const markersLength = this.markers.length
    for (let i = 0; i < markersLength; i++) {
      this.markers[i].setMap(null)
    }
    this.markers = []

    // 清空安装商数据
    this._installersData = []
  }
}

customElements.define('google-map-section', GoogleMapSection)
